import { Reservation } from "shared/reservation"

/**
 * LOCKER-522 — a reservation whose deposit code can still open the door to
 * drop the parcel off: reserved, not yet deposited, expired, collected or
 * cancelled. Mirrors when the drawer shows the deposit code.
 */
const PAST_DEPOSIT_STATUSES = [
  "DELIVERED",
  "EXPIRED",
  "CLOSE",
  "CLOSED",
  "CANCEL",
  "CANCELLED",
  "DELETED",
]

export const isDepositCodeUsable = (reservation?: Reservation) => {
  const sst = reservation?.sstReservation
  if (!sst?.delivery_code || sst.delivery_time) return false
  return !PAST_DEPOSIT_STATUSES.includes(sst.status || "")
}

/**
 * Auto-print happens on reservation CREATION, in the console that created it
 * (Paul, 2026-10-29: "as soon as it is created we need to print it"). Printing
 * on the deposit transition was the first cut and printed too late.
 *
 * The create call returns before the partner necessarily minted the codes, so
 * the form only queues the reservation id; the watcher prints once the
 * reservation in the store carries a deposit code (in the create response,
 * or later over MRT). Only the creating console queues, so a second
 * workstation never prints someone else's label.
 */
const QUEUE_TTL_MS = 10 * 60 * 1000

type Listener = () => void

const queue = new Map<string, number>()
const listeners = new Set<Listener>()
let version = 0

const notify = () => {
  version += 1
  listeners.forEach((l) => l())
}

export const queueAutoPrint = (reservationId: string, now = Date.now()) => {
  queue.set(reservationId, now)
  notify()
}

export const subscribeAutoPrintQueue = (listener: Listener) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export const getAutoPrintQueueVersion = () => version

/**
 * Removes and returns the queued reservations that are ready to print. Entries
 * older than the TTL are dropped: a code that never arrived is not printed
 * minutes later as a surprise.
 */
export const takeReadyAutoPrints = (
  reservations: Reservation[],
  now = Date.now(),
): Reservation[] => {
  const ready: Reservation[] = []
  for (const [id, queuedAt] of queue) {
    if (now - queuedAt > QUEUE_TTL_MS) {
      queue.delete(id)
      continue
    }
    const reservation = reservations.find((r) => r.id === id)
    if (reservation && isDepositCodeUsable(reservation)) {
      queue.delete(id)
      ready.push(reservation)
    }
  }
  return ready
}

/** Test seam. */
export const resetAutoPrintQueue = () => {
  queue.clear()
  version = 0
}

const AUTO_PRINTED_KEY_PREFIX = "sl2.depositCodeAutoPrinted."
/** A reservation is only ever re-queued within minutes; a day is generous. */
const AUTO_PRINTED_TTL_MS = 24 * 60 * 60 * 1000

const pruneAutoPrintClaims = (now: number) => {
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i)
    if (!key?.startsWith(AUTO_PRINTED_KEY_PREFIX)) continue
    if (now - Number(localStorage.getItem(key)) > AUTO_PRINTED_TTL_MS) {
      localStorage.removeItem(key)
    }
  }
}

/**
 * Guards against printing one reservation twice (a double submit, a re-queue).
 * Storage failure (private mode) only loses that guard, never the print.
 */
export const claimAutoPrint = (
  reservationId: string,
  now: number = Date.now(),
): boolean => {
  try {
    pruneAutoPrintClaims(now)
    const key = AUTO_PRINTED_KEY_PREFIX + reservationId
    if (localStorage.getItem(key)) return false
    localStorage.setItem(key, String(now))
  } catch {
    // storage unavailable: print anyway
  }
  return true
}
